import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, type AppEvent, type StockChannel, type StockStage } from "../api";
import { useAuth } from "../auth/AuthContext";
import {
  Column,
  sortEventsNewestFirst,
  statusMeta,
} from "../components/AccountFeed";
import { EventDetailModal, summaryFromEvent } from "../components/EventDetailModal";
import { useToast } from "../components/Toast";
import { isElectronDesktop } from "../desktop";
import { useLocalEventsQuery } from "../hooks/localEventsStore";
import { useLiveEvents } from "../hooks/useLiveEvents";

type StageTab = "ready" | "pending" | "bad";
type ChannelTab = "passkey" | "admin_user" | "all";

function stockStage(e: AppEvent): StockStage {
  return e.stockStage || "none";
}

function stockChannel(e: AppEvent): StockChannel {
  return e.stockChannel || "none";
}

function sortPendingOldestFirst(a: AppEvent, b: AppEvent): number {
  return a.id - b.id;
}

function mergeById(base: AppEvent[], extra: AppEvent[]): AppEvent[] {
  const map = new Map<number, AppEvent>();
  for (const e of base) map.set(e.id, e);
  for (const e of extra) map.set(e.id, e);
  return [...map.values()];
}

const STOCK_OPTS: {
  stockStage: string[];
  limit: number;
  includeHidden: boolean;
} = {
  stockStage: ["pending", "ready", "bad"],
  limit: 2000,
  includeHidden: false,
};

export function StockPage() {
  const { user, setAssignedKeys } = useAuth();
  const { push } = useToast();
  const [selected, setSelected] = useState<AppEvent | null>(null);
  const [filter, setFilter] = useState("");
  const [stageTab, setStageTab] = useState<StageTab>("pending");
  const [channelTab, setChannelTab] = useState<ChannelTab>("all");
  const [httpSeed, setHttpSeed] = useState<AppEvent[]>([]);
  const [overlay, setOverlay] = useState<AppEvent[]>([]);
  const [seedError, setSeedError] = useState<string | null>(null);
  const desktop = isElectronDesktop();
  const isAdmin = user?.role === "admin";

  const onAssignment = useCallback(
    (info: {
      resourceKey: string;
      message: string;
      assignedKeys: string[];
      events: AppEvent[];
    }) => {
      setAssignedKeys(info.assignedKeys);
      push(info.message, "ok");
    },
    [push, setAssignedKeys]
  );

  const onUnassignment = useCallback(
    (info: { resourceKey: string; message: string; assignedKeys: string[] }) => {
      setAssignedKeys(info.assignedKeys);
      push(info.message, "info");
    },
    [push, setAssignedKeys]
  );

  const { status, events: liveEvents, error } = useLiveEvents({
    enabled: Boolean(user) && isAdmin,
    onAssignment,
    onUnassignment,
  });

  const { events: localStock } = useLocalEventsQuery(
    STOCK_OPTS,
    Boolean(desktop && isAdmin)
  );

  // Browser / non-desktop: HTTP seed once (desktop uses local SQLite viewer only).
  useEffect(() => {
    if (!isAdmin || desktop) return;
    let cancelled = false;
    void (async () => {
      try {
        const stages: StageTab[] = ["pending", "ready", "bad"];
        const batches = await Promise.all(
          stages.map((stage) =>
            api<{ events: AppEvent[] }>(
              `/api/admin/events/stock?stage=${stage}&limit=1000`
            )
          )
        );
        if (!cancelled) {
          setHttpSeed(batches.flatMap((b) => b.events));
          setSeedError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setSeedError(err instanceof Error ? err.message : "Failed to load stock");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, desktop]);

  const stockSeed = mergeById(desktop ? localStock : httpSeed, overlay);
  const events = useMemo(
    () => mergeById(stockSeed, liveEvents),
    [stockSeed, liveEvents]
  );

  const meta = statusMeta(status);
  const selectedLive = selected
    ? events.find((e) => e.id === selected.id) || selected
    : null;

  const counts = useMemo(() => {
    const ready = events.filter((e) => stockStage(e) === "ready" && !e.hidden);
    const pending = events.filter((e) => stockStage(e) === "pending" && !e.hidden);
    const bad = events.filter((e) => stockStage(e) === "bad" && !e.hidden);
    return {
      ready: ready.length,
      pending: pending.length,
      bad: bad.length,
      readyPasskey: ready.filter((e) => stockChannel(e) === "passkey").length,
      readyAdmin: ready.filter((e) => stockChannel(e) === "admin_user").length,
      pendingPasskey: pending.filter((e) => stockChannel(e) === "passkey").length,
      pendingAdmin: pending.filter((e) => stockChannel(e) === "admin_user").length,
    };
  }, [events]);

  const items = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const stage: StockStage = stageTab;
    let list = events.filter((e) => !e.hidden && stockStage(e) === stage);

    if (channelTab !== "all") {
      list = list.filter((e) => stockChannel(e) === channelTab);
    }

    list = list.filter((e) => {
      if (!q) return true;
      const s = summaryFromEvent(e);
      const note = String(e.adminNotes || "").toLowerCase();
      return (
        s.id.toLowerCase().includes(q) ||
        s.email.toLowerCase().includes(q) ||
        s.type.toLowerCase().includes(q) ||
        note.includes(q) ||
        (e.assignedUsername || "").toLowerCase().includes(q)
      );
    });

    if (stageTab === "pending") return list.sort(sortPendingOldestFirst);
    return list.sort(sortEventsNewestFirst);
  }, [events, filter, stageTab, channelTab]);

  // Auto-advance respects the active channel filter (same as visible list)
  const pendingQueue = useMemo(() => {
    let list = events.filter((e) => !e.hidden && stockStage(e) === "pending");
    if (channelTab !== "all") {
      list = list.filter((e) => stockChannel(e) === channelTab);
    }
    return list.sort(sortPendingOldestFirst);
  }, [events, channelTab]);

  function openNextPending(afterId?: number) {
    const next =
      afterId != null
        ? pendingQueue.find((e) => e.id > afterId) ||
          pendingQueue.find((e) => e.id !== afterId)
        : pendingQueue[0];
    if (next) setSelected(next);
    else setSelected(null);
  }

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const channelCounts =
    stageTab === "ready"
      ? { passkey: counts.readyPasskey, admin: counts.readyAdmin, all: counts.ready }
      : stageTab === "pending"
        ? {
            passkey: counts.pendingPasskey,
            admin: counts.pendingAdmin,
            all: counts.pending,
          }
        : { passkey: 0, admin: 0, all: counts.bad };

  return (
    <div className="page page-wide page-fill">
      <header className="page-header">
        <div>
          <h1>Available Stock</h1>
          <p className="muted page-blurb">
            Station queue — work Pending FIFO, move to Ready or Mark Bad
          </p>
        </div>
        <div className="page-header-actions">
          <input
            className="search header-search"
            placeholder="Filter email, id, notes…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <span className={`status-chip ${meta.className}`}>
            <span className="status-dot" />
            {meta.label}
          </span>
        </div>
      </header>

      {(error || seedError) && (
        <div className="banner error">{error || seedError}</div>
      )}

      <div className="stock-tabs" role="tablist" aria-label="Stock stage">
        {(
          [
            ["pending", "Pending", counts.pending],
            ["ready", "Ready", counts.ready],
            ["bad", "Bad", counts.bad],
          ] as const
        ).map(([id, label, count]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={stageTab === id}
            className={`stock-tab${stageTab === id ? " active" : ""}`}
            onClick={() => {
              setStageTab(id);
              setChannelTab("all");
            }}
          >
            {label}
            <span className="stock-tab-count">{count}</span>
          </button>
        ))}
      </div>

      {stageTab !== "bad" && (
        <div className="stock-tabs stock-tabs-secondary" role="tablist" aria-label="Channel">
          {(
            [
              ["all", "All", channelCounts.all],
              ["passkey", "Passkey", channelCounts.passkey],
              ["admin_user", "Admin User", channelCounts.admin],
            ] as const
          ).map(([id, label, count]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={channelTab === id}
              className={`stock-tab${channelTab === id ? " active" : ""}`}
              onClick={() => setChannelTab(id)}
            >
              {label}
              <span className="stock-tab-count">{count}</span>
            </button>
          ))}
        </div>
      )}

      <div className="feed-board feed-board-single">
        <Column
          title={
            stageTab === "pending"
              ? "Pending — work oldest first"
              : stageTab === "ready"
                ? "Ready stock"
                : "Bad"
          }
          items={items}
          empty={
            stageTab === "pending"
              ? "No pending stock."
              : stageTab === "ready"
                ? "No ready stock yet."
                : "Nothing marked bad."
          }
          isAdmin
          onOpen={setSelected}
        />
      </div>

      {selectedLive && (
        <EventDetailModal
          event={selectedLive}
          onClose={() => setSelected(null)}
          stockMode
          onStockMoved={(moved) => {
            setOverlay((prev) => mergeById(prev, [moved]));
            if (stageTab === "pending") openNextPending(moved.id);
            else setSelected(null);
          }}
        />
      )}
    </div>
  );
}
